import { useMemo, useState, type ReactNode } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { CohortBoardPayload } from '@/components/CohortBoard';

type GraphNodeRow = NonNullable<CohortBoardPayload['graph_nodes']>[number];
type Stem = { kind?: string; node_id?: string; label: string; how?: string };
type Acquire = {
  via?: string;
  what?: string;
  where?: string;
  tool?: string;
  if_missing?: string;
};

type Props = {
  board: CohortBoardPayload;
  memoSlot?: ReactNode;
};

const TYPE_TONE: Record<string, string> = {
  Metrics: 'bg-sky-50 text-sky-800',
  Products: 'bg-violet-50 text-violet-800',
  Customers: 'bg-amber-50 text-amber-900',
  Geographies: 'bg-emerald-50 text-emerald-800',
  People: 'bg-rose-50 text-rose-800',
  'Exec changes': 'bg-rose-50 text-rose-800',
  Lawsuits: 'bg-orange-50 text-orange-900',
  'Insider trades': 'bg-fuchsia-50 text-fuchsia-800',
  '13F holders': 'bg-indigo-50 text-indigo-800',
  '13F positions': 'bg-indigo-50 text-indigo-800',
  Filings: 'bg-gray-100 text-gray-700',
};

export function isSecGraphBoard(board?: CohortBoardPayload | null): boolean {
  return board?.layout === 'sec_graph';
}

function formatValue(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '';
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return String(value);
  if (Math.abs(n) >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(2)}B`;
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return n.toLocaleString();
  return String(n);
}

export function SecGraphBoard({ board, memoSlot }: Props) {
  const typeCounts = board.type_counts || {};
  const types = Object.keys(typeCounts);
  const [active, setActive] = useState<string | 'all'>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const nodes = board.graph_nodes || [];
  const filtered = useMemo(() => {
    if (active === 'all') return nodes;
    return nodes.filter((n) => prettyType(n.type) === active);
  }, [active, nodes]);
  const selected = nodes.find((n) => n.id === selectedId) || null;

  return (
    <div data-sec-graph-board className="rounded-3xl border border-gray-200 bg-white p-5 shadow-sm">
      <header className="mb-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          SEC knowledge graph
          {board.as_of ? ` · ${board.as_of}` : ''}
          {board.cik ? ` · CIK ${board.cik}` : ''}
          {board.harvest_source ? ` · ${board.harvest_source}` : ''}
        </p>
        <h2 className="mt-1 text-xl font-semibold text-gray-900">
          {board.company_name || board.object}
        </h2>
        {board.headline ? (
          <p className="mt-2 text-sm leading-relaxed text-gray-700">{board.headline}</p>
        ) : null}
      </header>

      {board.stats?.length ? (
        <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
          {board.stats.map((stat) => (
            <div key={stat.label} className="rounded-2xl bg-gray-50 px-3 py-2">
              <div className="text-lg font-semibold text-gray-900">{stat.value}</div>
              <div className="text-[11px] uppercase tracking-wide text-gray-500">{stat.label}</div>
            </div>
          ))}
        </div>
      ) : null}

      {types.length ? (
        <div className="mb-4 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setActive('all')}
            className={`rounded-full px-3 py-1 text-xs font-medium ${
              active === 'all' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700'
            }`}
          >
            All
          </button>
          {types.map((label) => (
            <button
              type="button"
              key={label}
              onClick={() => setActive(label)}
              className={`rounded-full px-3 py-1 text-xs font-medium ${
                active === label
                  ? 'bg-gray-900 text-white'
                  : TYPE_TONE[label] || 'bg-gray-100 text-gray-700'
              }`}
            >
              {label} {typeCounts[label]}
            </button>
          ))}
        </div>
      ) : null}

      {board.charts?.length ? (
        <div className="mb-4 grid gap-4 md:grid-cols-2">
          {board.charts.map((chart) => (
            <div key={chart.title} className="h-48 rounded-2xl border border-gray-100 p-2">
              <p className="px-2 text-xs font-medium text-gray-600">{chart.title}</p>
              <ResponsiveContainer width="100%" height="85%">
                <BarChart data={chart.data}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#111827" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ))}
        </div>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(18rem,22rem)]">
        <div className="max-h-80 overflow-y-auto rounded-2xl border border-gray-100">
          <table className="w-full text-left text-sm">
            <thead className="sticky top-0 bg-gray-50 text-[11px] uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-3 py-2">Type</th>
                <th className="px-3 py-2">Node</th>
                <th className="px-3 py-2">Period</th>
                <th className="px-3 py-2">Value</th>
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0, 80).map((node) => (
                <tr
                  key={node.id}
                  className={`cursor-pointer border-t border-gray-100 ${
                    selectedId === node.id ? 'bg-gray-900/5' : 'hover:bg-gray-50'
                  }`}
                  onClick={() => setSelectedId(selectedId === node.id ? null : node.id)}
                >
                  <td className="px-3 py-1.5 text-xs text-gray-500">{prettyType(node.type)}</td>
                  <td className="px-3 py-1.5">
                    {node.url ? (
                      <a
                        href={node.url}
                        className="text-gray-900 underline-offset-2 hover:underline"
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {node.label}
                      </a>
                    ) : (
                      node.label
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-xs text-gray-500">{node.period || ''}</td>
                  <td className="px-3 py-1.5 text-xs text-gray-700">
                    {formatValue(node.value)}
                    {node.unit ? <span className="ml-1 text-gray-400">{node.unit}</span> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <NodeResearchPanel node={selected} />
      </div>

      {memoSlot ? <div className="mt-4">{memoSlot}</div> : null}
    </div>
  );
}

function NodeResearchPanel({ node }: { node: GraphNodeRow | null }) {
  if (!node) {
    return (
      <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 p-4 text-sm text-gray-500">
        Select a node to see what hits it, where the number comes from, who is related, and how to pull the next series.
      </div>
    );
  }
  const research = node.research;
  const restated = research?.restated || node.restated || '';
  return (
    <div className="max-h-80 overflow-y-auto rounded-2xl border border-gray-200 bg-white p-4 text-sm">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
        {prettyType(node.type)}
        {node.form ? ` · ${node.form}` : ''}
      </p>
      <h3 className="mt-1 font-semibold text-gray-900">{node.label}</h3>
      {(node.value != null && node.value !== '') || node.period ? (
        <p className="mt-1 text-xs text-gray-500">
          {[formatValue(node.value), node.unit, node.period].filter(Boolean).join(' · ')}
        </p>
      ) : null}
      {restated ? <p className="mt-3 leading-relaxed text-gray-800">{restated}</p> : null}
      {research?.identity ? (
        <p className="mt-2 text-xs text-gray-600">
          <span className="font-medium text-gray-800">Identity. </span>
          {research.identity}
        </p>
      ) : null}
      <StemBlock title="Comes from" stems={research?.comes_from} />
      <StemBlock title="Directly impacts" stems={research?.impacts} />
      <StemBlock title="Who is related" stems={research?.related} />
      <StemBlock title="Connected to" stems={research?.connects_to} />
      {research?.tied_data?.length ? (
        <div className="mt-3">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Tied data</p>
          <div className="mt-1 flex flex-wrap gap-1">
            {research.tied_data.map((item) => (
              <span key={item} className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] text-gray-700">
                {item}
              </span>
            ))}
          </div>
        </div>
      ) : null}
      {research?.acquire?.length ? (
        <div className="mt-3">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Acquire</p>
          <ul className="mt-1 space-y-2">
            {research.acquire.map((path, i) => (
              <AcquireRow key={`${path.via}-${path.what}-${i}`} path={path} />
            ))}
          </ul>
        </div>
      ) : null}
      {research?.world?.length ? (
        <div className="mt-3">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
            Outside the filings
          </p>
          <ul className="mt-1 space-y-1 text-xs text-gray-700">
            {research.world.map((tie, i) => (
              <li key={`${tie.kind}-${tie.label}-${tie.ticker}-${i}`}>
                <span className="font-medium text-gray-900">{tie.kind || "company"}</span>
                {tie.label ? <span> — {tie.label}</span> : null}
                {tie.ticker ? <span className="text-gray-500"> ({tie.ticker})</span> : null}
                {tie.how ? <span className="text-gray-400"> — {tie.how}</span> : null}
                {tie.where ? <div className="text-gray-500">{tie.where}</div> : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {research?.world_reasoning ? (
        <p className="mt-3 text-xs leading-relaxed text-gray-700">{research.world_reasoning}</p>
      ) : null}
      {research?.reasoning ? (
        <p className="mt-3 text-xs leading-relaxed text-gray-700">{research.reasoning}</p>
      ) : null}
      {research?.hole ? (
        <p className="mt-2 text-xs text-amber-900">
          <span className="font-medium">Still open. </span>
          {research.hole}
        </p>
      ) : null}
    </div>
  );
}

function StemBlock({ title, stems }: { title: string; stems?: Stem[] }) {
  if (!stems?.length) return null;
  return (
    <div className="mt-3">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{title}</p>
      <ul className="mt-1 space-y-0.5 text-xs text-gray-700">
        {stems.map((stem, i) => (
          <li key={`${stem.node_id}-${stem.label}-${i}`}>
            {stem.label}
            {stem.how ? <span className="text-gray-400"> — {stem.how}</span> : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

function AcquireRow({ path }: { path: Acquire }) {
  return (
    <li className="rounded-xl bg-gray-50 px-2 py-1.5 text-xs text-gray-700">
      <span className="font-medium text-gray-900">{path.via || 'edgar'}</span>
      {path.what ? <span> — {path.what}</span> : null}
      {path.where ? <span className="text-gray-500"> ({path.where})</span> : null}
      {path.tool ? <span className="text-gray-400"> · {path.tool}</span> : null}
      {path.if_missing ? <div className="mt-0.5 text-gray-500">If missing: {path.if_missing}</div> : null}
    </li>
  );
}

function prettyType(type: string): string {
  const labels: Record<string, string> = {
    issuer: 'Issuer',
    filing: 'Filings',
    period: 'Periods',
    metric: 'Metrics',
    product: 'Products',
    customer: 'Customers',
    geography: 'Geographies',
    segment: 'Segments',
    person: 'People',
    role_change: 'Exec changes',
    lawsuit: 'Lawsuits',
    insider_trade: 'Insider trades',
    holder: '13F holders',
    position: '13F positions',
    trend: 'Trends',
    risk: 'Risks',
    contract: 'Contracts',
    subsidiary: 'Subsidiaries',
    fact: 'Other facts',
  };
  return labels[type] || type;
}
